import '@/components/Chart/style.css';
import { memo, useMemo } from 'react';

import { Box } from '@mui/material';

import useUserDetails from '@/hooks/useUserDetails';
import ForwardCurveChartBlock from '@/pages/Markets/MarketsComponents/Chart/ForwardCurveChartBlock';
import { FullScreenChartContent } from '@/pages/Markets/MarketsComponents/Chart/FullScreenChartContent';
import SeasonalChartBlock from '@/pages/Markets/MarketsComponents/Chart/SeasonalChartBlock';
import { SpotChartBlock } from '@/pages/Markets/MarketsComponents/Chart/SpotChartBlock';
import useMarketsChart from '@/pages/Markets/MarketsComponents/Chart/useMarketsChart';
import { useMarketWidget } from '@/pages/Markets/MarketsComponents/contexts/MarketWidgetContext';
import { LightModeSwitch } from '@/pages/Markets/MarketsComponents/LightMode/LightModeSwitch';
import type { ChartsBlockTabType } from '@/pages/Markets/MarketsComponents/types';

type ChartsBlockProps<T> = {
  tab: ChartsBlockTabType<T>;
  layout?: { fullWidth?: boolean; height?: number };
};

const ChartsBlock = <T,>({ tab, layout }: ChartsBlockProps<T>) => {
  const { activeTab, templateChartSeries, metadata } = useMarketsChart(tab);
  const isForwardCurve = metadata?.graphType === 'forwardSimple';
  const isSeasonal = metadata?.graphType === 'seasonal';
  const chartDataRange = { from: metadata?.from ?? '5y', to: metadata?.to };
  const { isUserExpired } = useUserDetails();
  const activeTabName = useMemo(() => tab.name, [activeTab, tab]);
  const { isFullScreen } = useMarketWidget();
  if (isForwardCurve) {
    return (
      <ForwardCurveChartBlock
        key={`forwardCurveChartBlock${activeTab}`}
        layout={layout}
        templateChartSeries={templateChartSeries}
      />
    );
  } else if (isSeasonal) {
    return (
      <SeasonalChartBlock
        key={`seasonalChartBlock${activeTab}`}
        chartDataRange={chartDataRange}
        layout={layout}
        templateChartSeries={templateChartSeries}
      />
    );
  }

  if (isFullScreen && !isUserExpired) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
        <LightModeSwitch />
        <FullScreenChartContent
          key={activeTab}
          activeTab={activeTab}
          tabName={activeTabName}
          templateChartSeries={templateChartSeries}
        />
      </Box>
    );
  }
  return (
    <SpotChartBlock
      key={`spotChartBlock${activeTab}`}
      layout={layout}
      templateChartSeries={templateChartSeries}
    />
  );
};

export default memo(ChartsBlock);
